import { Suspense, lazy, memo } from "react";

import { AsyncDataSuspense } from "@tsp-core/async/components/async-data-suspense";
import { Datetime } from "@tsp-core/datetime/types";
import { createCurrentDatetime, endOfDay, startOfMonth, sub } from "@tsp-core/datetime/utils";

import { accountsApi } from "@tsp-domains/accounts/api";
import { contractorsAccountsApi } from "@tsp-domains/contractor-accounts/api";
import { contractsApi } from "@tsp-domains/contracts/api";
import { customWorksApi } from "@tsp-domains/custom-works/api";
import { departmentsApi } from "@tsp-domains/departments/api";
import { invoicesApi } from "@tsp-domains/invoices/api";
import { notificationsApi } from "@tsp-domains/notifications/api";
import { partiesApi } from "@tsp-domains/parties/api";
import { positionsApi } from "@tsp-domains/positions/api";
import { projectsPortfolioApi } from "@tsp-domains/projects-portfolio/api";
import { systemTypesApi } from "@tsp-domains/system-types/api";
import { workSubTypesApi, workTypesApi } from "@tsp-domains/work-types/api";

import { Access } from "../../features/auth/components";
import { Layout } from "../../features/layout";
import { UserStateData } from "../../features/user";

const Content = lazy(() => import("./work-items-listing.content"));

const checkAccess = (user: UserStateData) => user.authenticated;

function WorkItemsListingPage() {
  const currentDate = createCurrentDatetime();
  const period: [Datetime, Datetime] = [startOfMonth(sub(currentDate, { months: 12 })), endOfDay(currentDate)];

  const getDepartmentsDictionaryQuery = departmentsApi.useGetDepartmentsDictionaryQuery();
  const getExternalCwTypesQuery = customWorksApi.useGetExternalCwTypesQuery();
  const getNotificationListQuery = notificationsApi.useGetNotificationListQuery();
  const getPartiesDictionaryQuery = partiesApi.useGetPartiesDictionaryQuery();
  const getPositionsDictionaryQuery = positionsApi.useGetPositionsDictionaryQuery();
  const getProjectsPortfolioQuery = projectsPortfolioApi.useGetProjectsPortfolioQuery();
  const getWorkSubTypesDictionaryQuery = workSubTypesApi.useGetWorkSubTypesDictionaryQuery();
  const getWorkTypesDictionaryQuery = workTypesApi.useGetWorkTypesDictionaryQuery();
  const loadUserContractsQuery = contractsApi.useLoadUserContractsQuery();
  const loadSystemTypesQuery = systemTypesApi.useGetSystemTypesDictionaryQuery();
  const loadUserAccountsInfoQuery = accountsApi.useGetUserAccountsInfoQuery();
  const loadContractorsAccountsQuery = contractorsAccountsApi.useGetContractorsAccountsQuery();
  const getSupplierInvoiceNumbersQuery = invoicesApi.useGetSupplierInvoiceNumbersQuery(period);

  return (
    <Access checkAccess={checkAccess}>
      <Layout loading={false}>
        <AsyncDataSuspense
          queries={[
            getDepartmentsDictionaryQuery,
            getExternalCwTypesQuery,
            getNotificationListQuery,
            getPartiesDictionaryQuery,
            getPositionsDictionaryQuery,
            getProjectsPortfolioQuery,
            getWorkSubTypesDictionaryQuery,
            getWorkTypesDictionaryQuery,
            loadUserContractsQuery,
            loadSystemTypesQuery,
            loadUserAccountsInfoQuery,
            loadContractorsAccountsQuery,
            getSupplierInvoiceNumbersQuery,
          ]}
        >
          <Suspense fallback={null}>
            <Content supplierInvoiceNumbers={getSupplierInvoiceNumbersQuery.data ?? []} />
          </Suspense>
        </AsyncDataSuspense>
      </Layout>
    </Access>
  );
}

export default memo(WorkItemsListingPage);
